'use client';

import { faCircleInfo, faMessageLines } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NoDataRow, Table, TableCell, TableHeaderCell, TableHeaderRow, TableRow } from '@pickleballinc/features/src/table';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { formatPhoneFromParts } from '@pickleballinc/lib/phone-utils';
import { Avatar } from '@pickleballinc/ui/components/ui/avatar';
import { Button } from '@pickleballinc/ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@pickleballinc/ui/components/ui/tooltip';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { CaptainDesignationModal } from '@/components/forms';
import { ApproveDeclineRequestModal } from '@/components/molecules/modals/ApproveDeclineRequestModal';
import { SeasonListItem } from '@/entities/SeasonList';
import { CurrencyFormat } from '@/entities/shared/lookups/CurrencyFormat';
import {
	ActivityPlayerInfoData,
	ApproveDeclineStatusEnum,
	PlayerEligibilityFailureReason,
	PlayerTransferRequestType,
	TeamLeaguePlayerStatus,
	TeamPlayerInfo
} from '@/entities/TeamPlayerInfo';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useOrganizationStore } from '@/store/organization-store';
import { DateUtil } from '@/utils/date';
import { getPlayerStatusInfo, truncateToThreeDecimals } from '@/utils/helpers';
import { SeasonRegistrationSettingsPlayerEligibility } from '@/utils/types';
import { determinePriceDisplay } from '@/utils/utils';

type TeamData = {
	name: string;
	teamId: string;
	players: TeamPlayerInfo[] | ActivityPlayerInfoData[];
};

interface PlayerTableProps {
	teamData: TeamData;
	handleModalOpen: (title: string) => void;
	handleSendMessageModalOpen: (title: string, groupOfPlayers: boolean, playerId?: string) => void;
	teamLeagueId: string;
	seasonId: string;
	currency?: CurrencyFormat | null;
	seasonData: SeasonListItem | null;
}

export const getRequestTypeValue = (type: PlayerTransferRequestType) => {
	switch (type) {
		case PlayerTransferRequestType.ADD:
			return 'Add';
		case PlayerTransferRequestType.JOIN:
			return 'Join';
		case PlayerTransferRequestType.TRADE:
			return 'Trade';
		case PlayerTransferRequestType.MOVE:
			return 'Move';
		case PlayerTransferRequestType.DELETE:
			return 'Drop';
		case PlayerTransferRequestType.REPLACE:
			return 'Replace';
		case PlayerTransferRequestType.WITHDRAW:
			return 'Withdraw';
		case PlayerTransferRequestType.CAPTAIN_ADDED:
			return 'Invite';
		default:
			throw new Error('Invalid PlayerTransferRequestType');
	}
};

export const getRequestTypeValueInPast = (type: PlayerTransferRequestType) => {
	switch (type) {
		case PlayerTransferRequestType.ADD:
			return 'Added';
		case PlayerTransferRequestType.JOIN:
			return 'Joined';
		case PlayerTransferRequestType.TRADE:
			return 'Traded';
		case PlayerTransferRequestType.MOVE:
			return 'Moved';
		case PlayerTransferRequestType.DELETE:
			return 'Dropped';
		case PlayerTransferRequestType.REPLACE:
			return 'Replaced';
		case PlayerTransferRequestType.WITHDRAW:
			return 'Withdraw';
		case PlayerTransferRequestType.CAPTAIN_ADDED:
			return 'Invited';
		default:
			throw new Error('Invalid PlayerTransferRequestType');
	}
};

export const getEligibilityConditionText = (condition: SeasonRegistrationSettingsPlayerEligibility): string => {
	switch (condition) {
		case SeasonRegistrationSettingsPlayerEligibility.PlayerEligibility_BOTH:
			return 'waivers signed & payments';
		case SeasonRegistrationSettingsPlayerEligibility.PlayerEligibility_REGISTRATION_FEE_PAID:
			return 'payments made';
		case SeasonRegistrationSettingsPlayerEligibility.PlayerEligibility_WAIVERS_SIGNED:
			return 'waivers signed';
		default:
			return '';
	}
};

export const getEligibilityFailureReasonText = (reason: PlayerEligibilityFailureReason): string => {
	switch (reason) {
		case PlayerEligibilityFailureReason.PLAYER_ELIGIBILITY_FAILURE_REASON_BOTH:
			return 'Balance & Waivers';
		case PlayerEligibilityFailureReason.PLAYER_ELIGIBILITY_FAILURE_REASON_FEE_NOT_PAID:
			return 'Balance';
		case PlayerEligibilityFailureReason.PLAYER_ELIGIBILITY_FAILURE_REASON_WAIVERS_NOT_SIGNED:
			return 'Waivers';
		default:
			return '';
	}
};

export function TeamPlayersTable({
	teamData,
	handleSendMessageModalOpen,
	teamLeagueId,
	seasonId: _seasonId,
	currency,
	seasonData
}: PlayerTableProps) {
	const store = useOrganizationStore();
	const teamLeagueInfo = useTeamLeagueInfo();
	const tlTimeZone = store.teamLeagueTimeZone;
	const [isDesignationModalOpen, setDesignationModalOpen] = useState(false);
	const path = usePathname();
	const isPlayerActivity = path.includes('player-activity');

	const { showPlayerRatingsSingles, showPlayerRatingsDoubles, showPlayerRatingsSkinnySingles } = useTeamLeagueInfo();
	const [selectedPlayerForEdit, setSelectedPlayerForEdit] = useState<TeamPlayerInfo | ActivityPlayerInfoData | null>(null);
	const [selectedPlayerForRequest, setSelectedPlayerForRequest] = useState<TeamPlayerInfo | ActivityPlayerInfoData | null>(null);
	const isMLP = teamLeagueId === 'major-league-pickleball';

	const handlePlayerAction = (player: ActivityPlayerInfoData) => {
		setSelectedPlayerForEdit(player);
	};

	const handleEditClick = (player: TeamPlayerInfo) => {
		setSelectedPlayerForRequest(player);
		setDesignationModalOpen(true);
	};

	return (
		<>
			<Table>
				<thead className="bg-white">
					<TableHeaderRow>
						<TableHeaderCell className="w-8 text-center text-sm font-normal">Player name</TableHeaderCell>
						{teamLeagueInfo.useLineupStrength ? (
							<TableHeaderCell className="w-8 text-center text-sm font-normal">Lineup strength</TableHeaderCell>
						) : null}
						<TableHeaderCell className="w-8 text-center text-sm font-normal">Ratings</TableHeaderCell>
						{isPlayerActivity ? (
							<TableHeaderCell className="w-8 text-center text-sm font-normal">Status</TableHeaderCell>
						) : seasonData?.usePlayerEligibility ? (
							<TableHeaderCell className="w-8 text-center text-sm font-normal">
								<div className="flex items-center justify-center gap-2">
									<span>Eligibility</span>
									<TooltipProvider delayDuration={0} skipDelayDuration={0}>
										<Tooltip>
											<TooltipTrigger asChild>
												<FontAwesomeIcon icon={faCircleInfo} className="text-gray-600" />
											</TooltipTrigger>
											<TooltipPortal>
												<TooltipContent className="bg-gray-900">
													<p>
														This league requires {getEligibilityConditionText(seasonData.playerEligibility)} made to
														determine eligibility.
													</p>
												</TooltipContent>
											</TooltipPortal>
										</Tooltip>
									</TooltipProvider>
								</div>
							</TableHeaderCell>
						) : null}
						<TableHeaderCell className="w-8 text-center text-sm font-normal">{isPlayerActivity ? 'Payments' : 'Balance'}</TableHeaderCell>
						{isPlayerActivity ? (
							<TableHeaderCell className="w-8 text-center text-sm font-normal">Actions</TableHeaderCell>
						) : (
							<>
								<TableHeaderCell className="w-8 text-center text-sm font-normal">Availability</TableHeaderCell>
								<TableHeaderCell className="w-8 text-center text-sm font-normal">Captain/Co-captain</TableHeaderCell>
								<TableHeaderCell className="w-8 text-center text-sm font-normal" />
							</>
						)}
					</TableHeaderRow>
				</thead>
				<tbody>
					{!teamData.players || teamData.players.length === 0 ? (
						<NoDataRow placeholder="No players available" />
					) : (
						teamData.players.map((player, index) => (
							<TableRow
								key={`${(player as ActivityPlayerInfoData)?.transferRequestUuid}-${player.uuid}-${index}`}
								className={index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}
							>
								<TableCell>
									<div className="flex gap-4">
										{isPlayerActivity ? (
											<Avatar
												name={player.fullname}
												src={
													'photo' in player && player.photo?.profileImage?.url
														? getCDNURL(player.photo?.profileImage?.url, 200, 200)
														: undefined
												}
												size="lg"
												variant="gray"
											/>
										) : (
											<Avatar
												name={player.fullname}
												src={'thumbnail' in player && player.thumbnail ? getCDNURL(player.thumbnail, 200, 200) : undefined}
												size="lg"
												variant="gray"
											/>
										)}

										<div className="!pr-16 lg:!pr-5">
											<Link
												href={`/team-leagues/${teamLeagueId}/player-details/${player.playerUuid}/stats`}
												className="text-sm font-medium text-blue-600 hover:underline text-nowrap"
											>
												{player.fullname}
											</Link>

											<div
												onClick={() =>
													player.sendTextAlerts && handleSendMessageModalOpen(player.fullname, false, player.uuid)
												}
											>
												{!player.sendTextAlerts ? (
													<TooltipProvider delayDuration={0} skipDelayDuration={0}>
														<Tooltip>
															<TooltipTrigger asChild>
																<p className="text-xs text-gray-400">
																	{formatPhoneFromParts({
																		internationalCountryCallingCode: player.internationalCallingCode ?? '',
																		phone: player.phone ?? ''
																	})}
																</p>
															</TooltipTrigger>
															<TooltipPortal>
																<TooltipContent className="bg-gray-900">
																	<p>This player has opted out of SMS messages.</p>
																</TooltipContent>
															</TooltipPortal>
														</Tooltip>
													</TooltipProvider>
												) : (
													<div className="flex cursor-pointer items-center gap-2 text-darkorange-400">
														<p className="text-xs">
															{formatPhoneFromParts({
																internationalCountryCallingCode: player.internationalCallingCode ?? '',
																phone: player.phone ?? ''
															})}
														</p>
														<FontAwesomeIcon icon={faMessageLines} size="sm" />
													</div>
												)}
											</div>
										</div>
									</div>
								</TableCell>
								{teamLeagueInfo.useLineupStrength ? (
									<TableCell className="text-center">
										<p className="text-xs">{player.lineupStrength ?? '-'}</p>
									</TableCell>
								) : null}
								<TableCell className="flex justify-center items-center">
									<div className="text-left">
										{showPlayerRatingsDoubles ? (
											<p className="text-sm font-medium">
												(D) {player.ratingDoubles ? truncateToThreeDecimals(player.ratingDoubles) : 'NR'}
											</p>
										) : null}

										{showPlayerRatingsSingles ? (
											<p className="text-xs text-gray-500">(S) {truncateToThreeDecimals(player.ratingSingles) ?? 'NR'}</p>
										) : null}

										{!isMLP && showPlayerRatingsSkinnySingles ? (
											<p className="text-xs text-gray-500">(SS) {truncateToThreeDecimals(player.skinnySingles) ?? 'NR'}</p>
										) : null}
									</div>
								</TableCell>
								{isPlayerActivity || seasonData?.usePlayerEligibility ? (
									<TableCell className="text-center">
										{isPlayerActivity ? (
											// Show status column for player activity
											(player as ActivityPlayerInfoData).isCompleted &&
											((player as ActivityPlayerInfoData).isApprovedByCommissioner ===
												ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_DECLINED ||
												(player as ActivityPlayerInfoData).isApprovedByTeam ===
													ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_DECLINED ||
												(player as ActivityPlayerInfoData).isApprovedByPlayer ===
													ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_DECLINED) ? (
												<div className="flex items-center justify-center">
													<div className="size-1.5 rounded-full bg-error-500 shrink-0 mr-2" />
													<span className="text-xs font-medium">{`Declined - ${getRequestTypeValue((player as ActivityPlayerInfoData).transferType)}`}</span>
												</div>
											) : (player as ActivityPlayerInfoData).isCompleted &&
											  ((player as ActivityPlayerInfoData).isApprovedByTeam ===
													ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_APPROVED ||
													(player as ActivityPlayerInfoData).isApprovedByPlayer ===
														ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_APPROVED) ? (
												<div className="flex items-center justify-center">
													<div className="size-1.5 rounded-full bg-green-500 shrink-0 mr-2" />
													<span className="text-xs font-medium">{`Done - ${getRequestTypeValueInPast((player as ActivityPlayerInfoData).transferType)}`}</span>
												</div>
											) : (
												<div className="flex items-center justify-center">
													<div className="size-1.5 rounded-full bg-darkorange-300 shrink-0 mr-2" />
													<span className="text-xs font-medium">{`Pending - Player must approve ${getRequestTypeValue((player as ActivityPlayerInfoData).transferType)}`}</span>
												</div>
											)
										) : seasonData?.usePlayerEligibility ? (
											// Show eligibility column
											(player as TeamPlayerInfo).isEligibleToPlay ? (
												<div className="flex items-center justify-center">
													<div className="size-1.5 rounded-full bg-green-500 shrink-0 mr-2" />
													<span className="text-xs font-medium">Eligible</span>
												</div>
											) : (
												<div className="flex flex-col items-center justify-center">
													<div className="flex items-center justify-center">
														<div className="size-1.5 rounded-full bg-error-500 shrink-0 mr-2" />
														<span className="text-xs font-medium">Ineligible</span>
													</div>
													<span className="text-xs text-error-600 mt-1">
														{getEligibilityFailureReasonText((player as TeamPlayerInfo).eligibilityFailureReason)}
													</span>
												</div>
											)
										) : null}
									</TableCell>
								) : null}
								<TableCell className="text-center">
									<p className="text-xs">
										{currency ? determinePriceDisplay(player.payment / 100, currency) : (player.payment / 100)?.toFixed(2)}
									</p>
								</TableCell>
								{isPlayerActivity ? (
									<TableCell className="text-center">
										{!(player as ActivityPlayerInfoData)?.isCompleted ? (
											(player as ActivityPlayerInfoData)?.isApprovedByCommissioner ===
											ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_PENDING ? (
												<TooltipProvider delayDuration={0} skipDelayDuration={0}>
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																onClick={() => handlePlayerAction(player as ActivityPlayerInfoData)}
																variant="outline"
																disabled
																size="sm"
															>
																Approve/Decline
															</Button>
														</TooltipTrigger>
														<TooltipPortal>
															<TooltipContent className="bg-gray-900">
																<p className="text-center">
																	The commissioner must first approve <br /> the request on the manage side.
																</p>
															</TooltipContent>
														</TooltipPortal>
													</Tooltip>
												</TooltipProvider>
											) : (player as ActivityPlayerInfoData)?.isApprovedByCommissioner ===
													ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_APPROVED &&
											  (player as ActivityPlayerInfoData)?.isApprovedByTeam ===
													ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_APPROVED ? (
												<Button variant="secondary" disabled>
													Player payment pending
												</Button>
											) : (
												<Button
													onClick={() => handlePlayerAction(player as ActivityPlayerInfoData)}
													variant="outline"
													size="sm"
												>
													Approve/Decline
												</Button>
											)
										) : (
											<div className="text-xs font-normal leading-[18px] text-gray-900 flex flex-col items-center">
												<div>
													<p className="text-left text-nowrap">
														{`Effective ` +
															DateUtil.formatUTCInTimezone(
																(player as ActivityPlayerInfoData)?.transferRequestDateUpdated ??
																	(player as ActivityPlayerInfoData).transferRequestDateCreated,
																'MMM D YYYY',
																tlTimeZone?.ianaTzIdentifier
															)}
													</p>
													<p className="text-left text-nowrap">
														{DateUtil.formatUTCInTimezone(
															(player as ActivityPlayerInfoData)?.transferRequestDateUpdated ??
																(player as ActivityPlayerInfoData).transferRequestDateCreated,
															'h:mm A',
															tlTimeZone?.ianaTzIdentifier
														)}
													</p>
												</div>
											</div>
										)}
									</TableCell>
								) : (
									<>
										<TableCell className="text-center">
											<p className="text-xs">{(player as TeamPlayerInfo).availability}</p>
										</TableCell>
										<TableCell className="text-center">
											{(() => {
												const { label, className } = getPlayerStatusInfo(
													(player as TeamPlayerInfo).status as TeamLeaguePlayerStatus
												);
												return label ? <p className={`text-xs font-medium ${className}`}>{label}</p> : null;
											})()}
										</TableCell>
										<TableCell className="text-center">
											<button
												onClick={() => handleEditClick(player as TeamPlayerInfo)}
												className="rounded bg-blue-100 px-3 py-1 text-xs font-medium text-blue-600 hover:bg-blue-200"
											>
												Edit
											</button>
										</TableCell>
									</>
								)}
							</TableRow>
						))
					)}
				</tbody>
			</Table>
			{selectedPlayerForRequest && isDesignationModalOpen ? (
				<CaptainDesignationModal
					key={`${teamData.teamId}-${selectedPlayerForRequest.playerUuid}`}
					title="Designate captains / co-captains"
					teamName={teamData.name}
					teamId={teamData.teamId}
					onClose={() => setDesignationModalOpen(false)}
					playerStatus={selectedPlayerForRequest.status as TeamLeaguePlayerStatus}
					playerLineupStrength={selectedPlayerForRequest.lineupStrength}
					playerUuid={selectedPlayerForRequest.playerUuid!}
				/>
			) : null}

			{selectedPlayerForEdit ? (
				<ApproveDeclineRequestModal
					open={!!selectedPlayerForEdit}
					fullName={selectedPlayerForEdit.fullname}
					teamName={teamData.name}
					transferRequestUuid={(selectedPlayerForEdit as ActivityPlayerInfoData).transferRequestUuid}
					onClose={() => setSelectedPlayerForEdit(null)}
				/>
			) : null}
		</>
	);
}
